import React, { useState, useEffect, useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
} from "recharts";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content) return null;
  if (typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const lines = content.split("\n");

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1"></div>;

        if (trimmed.startsWith("### "))
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">
                •
              </span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg className="animate-spin h-5 w-5 text-[#59AD31]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }

  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }

  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }

  return "Could not load analysis at this time.";
}

// ───────────────────── Labels (ES) ─────────────────────
const xAxisLabelYear = "Years";
const yAxisLabelUsd = "Million USD";
const yAxisDxUsd = -yAxisLabelUsd.length;

function formatYAxisMillionsTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";

  const abs = Math.abs(n);
  const inMillions = abs >= 1_000_000 ? abs / 1_000_000 : abs;
  const maxFD = inMillions < 10 ? 2 : 1;

  return inMillions.toLocaleString("es-ES", {
    maximumFractionDigits: maxFD,
  });
}

// ───────── helper para ordenar series por valor máximo ─────────
function getOrderByMaxValue(data, keys) {
  if (!data || data.length === 0) return keys;
  return [...keys].sort((a, b) => {
    const maxA = Math.max(
      ...data.map((d) => (typeof d[a] === "number" ? d[a] : 0)),
    );
    const maxB = Math.max(
      ...data.map((d) => (typeof d[b] === "number" ? d[b] : 0)),
    );
    return maxB - maxA;
  });
}

// ───────────────────── tooltip custom (ya ordenado por valor) ─────────────────────
function CustomTooltip({ active, payload, label, chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 shadow-md text-[11px]">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>
      {sortedPayload.map((item) => {
        const isTarget = item.dataKey === "targetShare";
        let icon;

        if (isTarget) {
          icon = (
            <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
              <line x1="0" y1="1.5" x2="16" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="3,2" />
            </svg>
          );
        } else if (chartType === "line") {
          icon = (
            <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
              <line x1="0" y1="1" x2="16" y2="1" stroke={item.color} strokeWidth="2" />
            </svg>
          );
        } else {
          icon = (
            <span
              className="h-2 w-2 rounded-[2px]"
              style={{ backgroundColor: item.color }}
            />
          );
        }

        return (
          <div
            key={item.dataKey}
            className="mb-0.5 flex items-center justify-between gap-2"
          >
            <div className="flex items-center gap-1.5">
              {icon}
              <span className="text-[#7E8081]">{item.name}</span>
            </div>
            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? Math.abs(item.value).toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                  })
                : item.value}{" "}
              <span className="text-[#98999A]">{yAxisLabelUsd}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ───────── leyendas custom SOLO para ordenar las etiquetas ─────────
function OrderedLegend({ payload, order, hiddenKeys, onToggle, chartType = "bar" }) {
  if (!payload) return null;

  const ordered = [...payload].sort(
    (a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey),
  );

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {ordered.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
            title="Click to show/hide"
          >
            <span className="text-[12px]">{entry.value}</span>
            {chartType === "line" ? (
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color || "#9999CC"} strokeWidth="2" />
              </svg>
            ) : (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ───────── colores simples por key (pool repetible) ─────────
const COLOR_POOL = [
  "#80D5FF",
  "#FB752E",
  "#FFD786",
  "#FF5579",
  "#009B8A",
  "#CBF3DD",
  "#7C3AED",
  "#F59E0B",
  "#10B981",
  "#EF4444",
];

function colorForKey(key) {
  if (key === "others") return "#80D5FF";
  let hash = 0;
  for (let i = 0; i < String(key).length; i++) {
    hash = (hash * 31 + String(key).charCodeAt(i)) >>> 0;
  }
  return COLOR_POOL[hash % COLOR_POOL.length];
}

function displayName(key) {
  if (key === "others") return "Others";
  return key; // producto tal cual (viene del backend)
}

export default function TradeTab({ data, pathway, pathwayId, country }) {
  const [hiddenTradeKeys, setHiddenTradeKeys] = useState([]);
  const [hiddenExportKeys, setHiddenExportKeys] = useState([]);
  const [hiddenImportKeys, setHiddenImportKeys] = useState([]);

  // 🔄 Limpiar estados cuando cambian los datos
  useEffect(() => {
    setHiddenTradeKeys([]);
    setHiddenExportKeys([]);
    setHiddenImportKeys([]);
  }, [data]);

  const tradeBalanceRaw = data ? data["Trade balance"] : [];

  const tradeBalanceData = useMemo(
    () =>
      (tradeBalanceRaw ?? []).map((d) => ({
        year: d.year,
        exports: Number(d.exports) || 0,
        imports: -(Number(d.imports) || 0),
       
      })),
    [tradeBalanceRaw],
  );

  const exportsData = data ? data["Exports by main products"] : [];
  const importsData = data ? data["Imports by main products"] : [];

  // ✅ keys dinámicas: todo lo que venga en la fila menos year
  const exportKeys = useMemo(() => {
    const first = exportsData?.[0];
    if (!first) return [];
    const keys = Object.keys(first).filter((k) => k !== "year");
    // pon "others" al final si existe
    return keys.sort((a, b) => (a === "others" ? 1 : b === "others" ? -1 : 0));
  }, [exportsData]);

  const importKeys = useMemo(() => {
    const first = importsData?.[0];
    if (!first) return [];
    const keys = Object.keys(first).filter((k) => k !== "year");
    return keys.sort((a, b) => (a === "others" ? 1 : b === "others" ? -1 : 0));
  }, [importsData]);

  // Orden de leyendas por máximo valor
  const tradeLegendOrder = useMemo(
    () => getOrderByMaxValue(tradeBalanceData, ["imports", "exports"]),
    [tradeBalanceData],
  );

  const exportsLegendOrder = useMemo(
    () => getOrderByMaxValue(exportsData, exportKeys),
    [exportsData, exportKeys],
  );

  const importsLegendOrder = useMemo(
    () => getOrderByMaxValue(importsData, importKeys),
    [importsData, importKeys],
  );

  const [tradeAnalysis, setTradeAnalysis] = useState("");
  const [exportsAnalysis, setExportsAnalysis] = useState("");
  const [importsAnalysis, setImportsAnalysis] = useState("");
  const [loadingTradeAnalysis, setLoadingTradeAnalysis] = useState(false);
  const [loadingExportsAnalysis, setLoadingExportsAnalysis] = useState(false);
  const [loadingImportsAnalysis, setLoadingImportsAnalysis] = useState(false);

  const fetchTradeAnalysis = async () => {
    if (!tradeBalanceData || tradeBalanceData.length === 0 || !pathwayId || !country) {
      setTradeAnalysis("");
      setLoadingTradeAnalysis(false);
      return;
    }

    setLoadingTradeAnalysis(true);
    try {
      const payload = {
        analysisKey: "trade_balance_analysis",
        scope: "country",
        country: country,
        tradeBalanceData: {
          data: JSON.stringify(tradeBalanceData),
          title: `Trade balance - ${country}`,
        },
        context: "trade",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Trade balance",
          },
        },
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/fablegpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Analysis API error: ${response.status}`);
      const resData = await response.json();
      setTradeAnalysis(extractAnalysisText(resData, ["tradeBalanceAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching trade balance analysis:", error);
      setTradeAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingTradeAnalysis(false);
    }
  };

  const fetchExportsAnalysis = async () => {
    if (!exportsData || exportsData.length === 0 || !pathwayId || !country) {
      setExportsAnalysis("");
      setLoadingExportsAnalysis(false);
      return;
    }

    setLoadingExportsAnalysis(true);
    try {
      const payload = {
        analysisKey: "exports_analysis",
        scope: "country",
        country: country,
        exportsData: {
          data: JSON.stringify(exportsData),
          title: `Exports by main products - ${country}`,
        },
        context: "trade",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Exports by main products",
          },
        },
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/fablegpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Analysis API error: ${response.status}`);
      const resData = await response.json();
      setExportsAnalysis(extractAnalysisText(resData, ["exportsAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching exports analysis:", error);
      setExportsAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingExportsAnalysis(false);
    }
  };

  const fetchImportsAnalysis = async () => {
    if (!importsData || importsData.length === 0 || !pathwayId || !country) {
      setImportsAnalysis("");
      setLoadingImportsAnalysis(false);
      return;
    }

    setLoadingImportsAnalysis(true);
    try {
      const payload = {
        analysisKey: "imports_analysis",
        scope: "country",
        country: country,
        importsData: {
          data: JSON.stringify(importsData),
          title: `Imports by main products - ${country}`,
        },
        context: "trade",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Imports by main products",
          },
        },
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/fablegpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Analysis API error: ${response.status}`);
      const resData = await response.json();
      setImportsAnalysis(extractAnalysisText(resData, ["importsAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching imports analysis:", error);
      setImportsAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingImportsAnalysis(false);
    }
  };

  useEffect(() => {
    fetchTradeAnalysis();
    // eslint-disable-next-line
  }, [tradeBalanceData, pathway, pathwayId, country]);

  useEffect(() => {
    fetchExportsAnalysis();
    // eslint-disable-next-line
  }, [exportsData, pathway, pathwayId, country]);

  useEffect(() => {
    fetchImportsAnalysis();
    // eslint-disable-next-line
  }, [importsData, pathway, pathwayId, country]);

  const handleLegendClickTrade = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenTradeKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickExports = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenExportKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickImports = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenImportKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const hasTradeBalance = tradeBalanceData?.length > 0;
  const hasExports = exportsData?.length > 0 && exportKeys?.length > 0;
  const hasImports = importsData?.length > 0 && importKeys?.length > 0;

  return (
    <div className="flex w-full flex-col gap-10">
      {/* ─── BLOQUE 1: Trade balance ─── */}
      <section className="flex w-full flex-col gap-4">
        <h2 className="text-[18px] font-medium text-[#323334]">
          Commodities Trade
        </h2>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          {hasTradeBalance ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={tradeBalanceData}
                stackOffset="sign"
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value={xAxisLabelYear}
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) => {
                    const n = Number(v);
                    if (!Number.isFinite(n)) return "";

                    // mostrar sin signo (porque exports está negativo solo para dirección visual)
                    const abs = Math.abs(n);

                    // Si son valores muy grandes, asume USD y convierte a millones.
                    // Si son ya pequeños (ej 0–500), asume que ya están en millones.
                    const inMillions = abs >= 1_000_000 ? abs / 1_000_000 : abs;

                    // Si aún así es pequeño, muestra 2 decimales; si no, 1 decimal
                    const maxFD = inMillions < 10 ? 2 : 1;

                    return inMillions.toLocaleString("es-ES", {
                      maximumFractionDigits: maxFD,
                    });
                  }}
                >
                  <Label
                    value={yAxisLabelUsd}
                    angle={-90}
                    position="left"
                    dx={yAxisDxUsd}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} chartType="bar" />}
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={tradeLegendOrder}
                      hiddenKeys={hiddenTradeKeys}
                      onToggle={handleLegendClickTrade}
                      chartType="bar"
                    />
                  )}
                />

                <Bar
                  dataKey="imports"
                  name="Imports"
                  fill="#80D5FF"
                  stackId="stack"
                  hide={hiddenTradeKeys.includes("imports")}
                />
                <Bar
                  dataKey="exports"
                  name="Exports"
                  fill="#FB752E"
                  stackId="stack"
                  hide={hiddenTradeKeys.includes("exports")}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7E8081]">
              No trade balance data available.
            </div>
          )}
        </div>

        <div className="w-full min-h-[100px]">
          {loadingTradeAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchTradeAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <NativeAnalysis content={tradeAnalysis} />
            </div>
          )}
        </div>
      </section>

      {/* ─── BLOQUE 2: Exports by main products ─── */}
      <section className="flex w-full flex-col gap-4">
        <h2 className="text-[18px] font-medium text-[#323334]">
          Exports of main commodities
        </h2>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          {hasExports ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={exportsData}
                stackOffset="none"
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value={xAxisLabelYear}
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={50}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={formatYAxisMillionsTick}
                >
                  <Label
                    value={yAxisLabelUsd}
                    angle={-90}
                    position="left"
                    dx={yAxisDxUsd}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} chartType="bar" />}
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={exportsLegendOrder}
                      hiddenKeys={hiddenExportKeys}
                      onToggle={handleLegendClickExports}
                      chartType="bar"
                    />
                  )}
                />

                {exportKeys.map((k) => (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="a"
                    fill={colorForKey(k)}
                    name={displayName(k)}
                    hide={hiddenExportKeys.includes(k)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7E8081]">
              No exports data available.
            </div>
          )}
        </div>

        <div className="w-full min-h-[100px]">
          {loadingExportsAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchExportsAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <NativeAnalysis content={exportsAnalysis} />
            </div>
          )}
        </div>
      </section>

      {/* ─── BLOQUE 3: Imports by main products ─── */}
      <section className="flex w-full flex-col gap-4">
        <h2 className="text-[18px] font-medium text-[#323334]">
          Imports of main commodities
        </h2>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          {hasImports ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={importsData}
                stackOffset="none"
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label
                    value={xAxisLabelYear}
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={50}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={formatYAxisMillionsTick}
                >
                  <Label
                    value={yAxisLabelUsd}
                    angle={-90}
                    position="left"
                    dx={yAxisDxUsd}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} chartType="bar" />}
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={importsLegendOrder}
                      hiddenKeys={hiddenImportKeys}
                      onToggle={handleLegendClickImports}
                      chartType="bar"
                    />
                  )}
                />

                {importKeys.map((k) => (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="a"
                    fill={colorForKey(k)}
                    name={displayName(k)}
                    hide={hiddenImportKeys.includes(k)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7E8081]">
              No imports data available.
            </div>
          )}
        </div>

        <div className="w-full min-h-[100px]">
          {loadingImportsAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchImportsAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <NativeAnalysis content={importsAnalysis} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
